// Chatbot.styled
import styled from 'styled-components';
import { Button } from '@mui/material';
import { MINT_MIST } from '../../constants/brandSurfaces';

const blue = '#0d4efe';
const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';
const line = 'rgba(26, 26, 46, 0.08)';
const brandGradient = `linear-gradient(135deg, ${teal} 0%, ${blue} 100%)`;

export const ChatBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(26, 26, 46, 0.35);
  backdrop-filter: blur(6px);
  z-index: 999; /* ChatShell is 1000 */
`;

export const ChatShell = styled.div`
  position: fixed;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid ${line};
  box-shadow:
    0 24px 60px rgba(26, 26, 46, 0.18),
    0 4px 16px rgba(13, 78, 254, 0.08);

  /* Smooth transition between docked <-> fullscreen */
  transition:
    width 320ms cubic-bezier(0.2, 0.8, 0.2, 1),
    height 320ms cubic-bezier(0.2, 0.8, 0.2, 1),
    right 320ms cubic-bezier(0.2, 0.8, 0.2, 1),
    bottom 320ms cubic-bezier(0.2, 0.8, 0.2, 1),
    left 320ms cubic-bezier(0.2, 0.8, 0.2, 1),
    top 320ms cubic-bezier(0.2, 0.8, 0.2, 1),
    border-radius 320ms cubic-bezier(0.2, 0.8, 0.2, 1),
    transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1);

  will-change: width, height, right, bottom, left, top, border-radius, transform;
  transform-origin: bottom right;

  &[data-mode='docked'] {
    right: 24px;
    bottom: 24px;
    width: 380px;
    height: min(600px, calc(100vh - 48px));
    border-radius: 20px;
    transform: translateZ(0);
  }

  &[data-mode='fullscreen'] {
    top: 24px;
    left: 24px;
    right: 24px;
    bottom: 24px;
    width: auto;
    height: auto;
    border-radius: 20px;
    transform: translateZ(0);
  }

  @media (max-width: 480px) {
    /* Docked is effectively fullscreen on phones */
    &[data-mode='docked'],
    &[data-mode='fullscreen'] {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: none;
      border-radius: 0;
      width: auto;
      height: auto;
    }
  }
`;

/** Floating "NKY Neighbor" button shown while the chat is closed. */
export const CollapsedPill = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 52px;
  padding: 0 18px 0 7px;
  border-radius: 999px;
  border: 1px solid ${line};
  background: #ffffff;
  color: ${ink};
  font-family: inherit;
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(26, 26, 46, 0.14);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  .chat-pill-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: ${brandGradient};
    color: #ffffff;
  }

  svg {
    font-size: 1.2rem;
  }

  &:hover,
  &:focus-visible {
    transform: translateY(-2px);
    border-color: rgba(13, 78, 254, 0.3);
    box-shadow: 0 12px 28px rgba(13, 78, 254, 0.18);
  }

  &:focus-visible {
    outline: 3px solid rgba(13, 78, 254, 0.35);
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    bottom: 16px;
    right: 16px;
    height: 48px;
    font-size: 0.92rem;

    .chat-pill-icon {
      width: 34px;
      height: 34px;
    }
  }
`;

export const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 12px 14px 16px;
  background: ${MINT_MIST};
  border-bottom: 1px solid ${line};
`;

export const HeaderIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${brandGradient};
  color: #ffffff;

  svg {
    font-size: 1.25rem;
  }
`;

export const HeaderText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const HeaderTitle = styled.h2`
  margin: 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.08rem;
  font-weight: 700;
  line-height: 1.25;
  color: ${ink};
`;

export const HeaderSubtitle = styled.span`
  font-size: 0.84rem;
  color: ${muted};
`;

export const HeaderActions = styled.div`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;

  .MuiIconButton-root {
    color: ${muted};
  }

  .MuiIconButton-root:hover {
    color: ${ink};
    background: rgba(26, 26, 46, 0.06);
  }
`;

export const MessagesContainer = styled.div`
  flex: 1;
  padding: 18px 16px;
  background: #ffffff;
  overflow-y: auto;
`;

export const Message = styled.div`
  display: flex;
  ${({ role }) => (role === 'user' ? 'justify-content: flex-end;' : 'justify-content: flex-start;')}
`;

const bubbleBase = `
  padding: 10px 14px;
  max-width: 82%;
  font-size: 0.96rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
  overflow: hidden;

  p {
    margin: 0 0 8px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    margin: 4px 0 8px;
    padding-left: 20px;
  }
`;

export const UserBubble = styled.div`
  ${bubbleBase}
  background: ${blue};
  color: #ffffff;
  border-radius: 16px 16px 4px 16px;
`;

export const BotBubble = styled.div`
  ${bubbleBase}
  background: ${MINT_MIST};
  color: ${ink};
  border: 1px solid rgba(26, 26, 46, 0.05);
  border-radius: 16px 16px 16px 4px;

  a {
    color: ${blue};
    font-weight: 600;
  }
`;

export const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid ${line};
  background: #ffffff;
`;

export const TextInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1.5px solid rgba(26, 26, 46, 0.12);
  background: #f7fafc;
  font-family: inherit;
  font-size: 1rem;
  color: ${ink};
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #8a93a6;
  }

  &:focus {
    outline: none;
    background: #ffffff;
    border-color: ${blue};
    box-shadow: 0 0 0 3px rgba(13, 78, 254, 0.12);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const SendButton = styled(Button)`
  && {
    flex-shrink: 0;
    min-width: 0;
    width: 46px;
    height: 46px;
    padding: 0;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${brandGradient};
    box-shadow: 0 4px 14px rgba(13, 78, 254, 0.35);
    transition: filter 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
  }

  & .MuiSvgIcon-root {
    font-size: 1.25rem;
    color: #ffffff;
  }

  &&:hover:not(:disabled) {
    filter: brightness(1.05);
    box-shadow: 0 6px 18px rgba(13, 78, 254, 0.4);
  }

  &&:disabled {
    opacity: 0.4;
    box-shadow: none;
  }
`;

export const MessageGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;
  ${({ role }) => (role === 'user' ? 'align-items: flex-end;' : 'align-items: flex-start;')}
`;

export const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 4px 6px;
  font-size: 0.76rem;
  color: ${muted};
`;

export const AvatarDot = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background: ${({ role }) => (role === 'user' ? blue : brandGradient)};
  flex: 0 0 auto;
`;

export const SenderName = styled.span`
  font-weight: 700;
  color: ${ink};
`;

export const TimeStamp = styled.span`
  font-weight: 400;
  color: #8a93a6;
`;

export const TypingIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 18px;

  & span {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: ${blue};
    opacity: 0.35;
    animation: nky-bounce 1s infinite ease-in-out;
  }

  & span:nth-child(2) {
    animation-delay: 0.15s;
  }

  & span:nth-child(3) {
    animation-delay: 0.3s;
  }

  @keyframes nky-bounce {
    0%,
    80%,
    100% {
      transform: translateY(0);
      opacity: 0.35;
    }
    40% {
      transform: translateY(-6px);
      opacity: 1;
    }
  }
`;
